import React, { useCallback, useEffect, useState } from 'react';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../components/bootstrap/Card';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import { Access, Module, Permission, Role } from './roleTypes';
import { centralApi } from '../AxiosInstance/CreateInstance';
import { toast } from 'react-toastify';
import Button from '../../components/bootstrap/Button';
import { useNavigate, useParams } from 'react-router-dom';
import useDarkMode from '../../hooks/useDarkMode';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { SpinnerCircular } from 'spinners-react';
import { File } from 'lucide-react';
import Page from '../../layout/Page/Page';

interface IRoleCreateProp {
	isFluid?: boolean;
}

const RoleCreate: React.FC<IRoleCreateProp> = ({ isFluid }) => {
	const [roles, setRoles] = useState<Role[]>([]);
	const [loading, setLoading] = useState(true);
	const [availableModules, setAvailableModules] = useState<Module[]>([]);
	const [addAccess, setAddAccess] = useState<Access[]>([]);
	const navigate = useNavigate();
	const { darkModeStatus } = useDarkMode();
	const [newRoleName, setNewRoleName] = useState('');
	const [addRoleError, setAddRoleError] = useState<string>('');
	const [roleNameTouched, setRoleNameTouched] = useState(false);
	const { id } = useParams();

	const fetchModules = async () => {
		try {
			const response = await centralApi.get(`userService/modulePermission/list`);
			let modules = response.data.data;
			const userDataString = localStorage.getItem('HRMS-data');
			const userData = userDataString ? JSON.parse(userDataString) : null;
			const isSuperAdmin = userData?.userType === 'superadmin';

			if (!isSuperAdmin) {
				modules = modules.filter((mod: Module) => mod.name?.toLowerCase() !== 'user list');
			}

			setAvailableModules(modules);
			return modules;
		} catch (error) {
			toast.error('Failed to fetch modules', {
				closeOnClick: true,
			});
			return [];
		}
	};

	const fetchRoles = useCallback(
		async (modules: Module[]) => {
			setLoading(true);
			try {
				const response = await centralApi.get(
					`userService/role/list?pageNo=${1}&limit=${100}`,
				);
				const activeRoles = response.data.data.filter(
					(role: Role) =>
						!role.isDeleted &&
						role.access.every(
							(access) => access.module && typeof access.module?.name === 'string',
						),
				);
				setRoles(activeRoles);

				if (id) {
					const roleToEdit = activeRoles?.find((role: Role) => role._id === id);
					if (roleToEdit) {
						const initializedAccess: Access[] = modules.map((module) => {
							const existingModule = roleToEdit.access.find(
								(accessItem: Access) => accessItem.module._id === module._id
							);

							return {
								_id: module._id,
								module,
								permissions:
									module.permissions?.map((permName) => {
										const matchingPermission = existingModule?.permissions.find(
											(p: Permission) => p.name === permName
										);
										return {
											_id: permName,
											name: permName,
											status: matchingPermission?.status || 'false',
										};
									}) || [],
							};
						});

						setAddAccess(initializedAccess);
						setNewRoleName(roleToEdit.name);
					}
				}
			} catch (error) {
				toast.error('Failed to fetch roles', {
					closeOnClick: true,
				});
			} finally {
				setLoading(false);
			}
		},
		[id],
	);

	const validateNewRoleName = (name: string) => {
		if (!name.trim()) {
			return 'Role name is required';
		}
		if (name.length < 3) {
			return 'Role name must be at least 3 characters long';
		}
		if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
			return 'Invalid role name. Please use letters only.';
		}
		const validNameRegex = /^(?=.*[a-zA-Z])[a-zA-Z\s-]+$/;
		if (!validNameRegex.test(name)) {
			return 'Role should contain only letters, spaces, or hyphens';
		}
		const existingRole = roles?.find(
			(role) => role.name.toLowerCase() === name.trim().toLowerCase() && role._id !== id,
		);
		if (existingRole) {
			return 'Role name already exists';
		}
		return '';
	};

	useEffect(() => {
		const initializeData = async () => {
			setLoading(true);
			const modules = await fetchModules();
			await fetchRoles(modules);
		};
		initializeData();
	}, [fetchRoles]);

	const toggleAddPermission = (moduleId: string, permissionName: string) => {
		setAddAccess((prevAccess) => {
			const module = availableModules.find((m) => m._id === moduleId);
			if (!module) return prevAccess;

			const moduleAccess = prevAccess.find((access) => access.module._id === moduleId);

			if (!moduleAccess) {
				const newAccess: Access = {
					_id: moduleId,
					module,
					permissions: module.permissions.map((perm) => ({
						_id: perm,
						name: perm,
						status: perm === permissionName ? 'true' : 'false',
					})),
				};
				return [...prevAccess, newAccess];
			}

			return prevAccess.map((access) => {
				if (access.module._id === moduleId) {
					return {
						...access,
						permissions: access.permissions.map((perm) =>
							perm.name === permissionName
								? { ...perm, status: perm.status === 'true' ? 'false' : 'true' }
								: perm
						),
					};
				}
				return access;
			});
		});
	};

	const handleSaveRole = async () => {
		const error = validateNewRoleName(newRoleName);
		setAddRoleError(error);

		if (error) {
			toast.error('Error while attaching role', {
				closeOnClick: true,
			});
			return;
		}

		if (addAccess.length === 0) {
			toast.error('Please select at least one permission', {
				closeOnClick: true,
			});
			setAddRoleError('Please select at least one permission');
			return;
		}

		try {
			const formattedAccess = addAccess
				.filter((access) => access?.permissions?.some((perm) => perm.status === 'true'))
				.map((access) => ({
					module: access?.module._id,
					permissions: access?.permissions
						.filter((perm) => perm?.status === 'true')
						.map((perm) => perm?._id),
				}));

			if (formattedAccess.length === 0) {
				toast.error('Please select at least one permission', {
					closeOnClick: true,
				});
				setAddRoleError('Please select at least one permission');
				return;
			}

			if (id) {
				await centralApi.patch(`userService/role/update`, {
					name: newRoleName,
					access: formattedAccess,
					roleId: id,
				});
				toast.success('Role updated successfully', {
					closeOnClick: true,
				});
			} else {
				await centralApi.post(`userService/role/add`, {
					name: newRoleName,
					access: formattedAccess,
				});
				toast.success('Role added successfully', {
					closeOnClick: true,
				});
			}

			setNewRoleName('');
			setAddAccess([]);
			setAddRoleError('');
			navigate('/role');
		} catch (err) {
			toast.error(id ? 'Failed to update role' : 'Failed to add role', {
				closeOnClick: true,
			});
		}
	};

	function capitalizeWords(str: string) {
		if (!str) return '';
		return str
			.toLowerCase()
			.split(' ')
			.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	return (
		<Page container='fluid'>
			<Card stretch={isFluid}>
				<CardHeader borderSize={1}>
					<CardLabel icon='Person' iconColor='info'>
						<CardTitle>{id ? 'Edit Role' : 'Create Role'}</CardTitle>
					</CardLabel>
				</CardHeader>
				<SubHeaderLeft className='mt-3 ms-4'>
					<Button
						className='back-btn'
						icon='ArrowBack'
						onClick={() => navigate('/role')}
					>
						Back to List
					</Button>
				</SubHeaderLeft>
				<CardBody>
					{/* <Card> */}
					<CardBody>
						<form>
							<div className='row'>
								<div className='col-4'>
									<FormGroup id='roleName' label='Role Name' className=''>
										<Input
											type='text'
											placeholder='Enter role name'
											value={capitalizeWords(newRoleName)}
											onChange={(
												e: React.ChangeEvent<HTMLInputElement>,
											) => {
												const inputValue = e.target.value;
												const formattedValue = inputValue
													.split(' ')
													.map(
														(word: any) =>
															word.charAt(0).toUpperCase() +
															word.slice(1).toLowerCase(),
													)
													.join(' ');
												setNewRoleName(formattedValue);
												setAddRoleError(
													validateNewRoleName(formattedValue),
												);
												if (roleNameTouched) {
													setAddRoleError(
														validateNewRoleName(formattedValue),
													);
												}
											}}
											onBlur={() => {
												setRoleNameTouched(true);
												setAddRoleError(
													validateNewRoleName(newRoleName),
												);
											}}
										/>
									</FormGroup>
									{addRoleError && (
										<div className='text-danger small mx-2'>
											{addRoleError}
										</div>
									)}
								</div>
							</div>
						</form>
					</CardBody>
					{/* </Card> */}
					{/* <Card> */}
					<CardHeader borderSize={1}>
						<CardLabel icon='Security' iconColor='info' className=''>
							<CardTitle>Permissions</CardTitle>
						</CardLabel>
					</CardHeader>
					<CardBody>
						{loading ? (
							<div className="d-flex w-100 flex-column justify-content-center align-items-center" style={{ minHeight: '100%' }}>
								<SpinnerCircular enabled={loading} color="#45b7d1" thickness={100} size={50} />
								<p className="mt-3">Loading...</p>
							</div>
						) : (
							availableModules.map((module) => {
								const moduleAccess = addAccess.find((access) => access.module._id === module._id);

								return (
									<div key={module._id} className="border rounded p-3 mb-4 role-bg">
										<div className="form-check d-flex align-items-center mb-3">
											{/* <input
													type="checkbox"
													className="form-check-input me-2"
													id={`module-checkbox-${module._id}`}
												
													checked={moduleAccess?.permissions?.every((p) => p.status === 'true') || false}
													onChange={() => {
														if (moduleAccess) {
															setAddAccess(addAccess.filter((access) => access.module._id !== module._id));
														} else {
															const newAccess: Access = {
																_id: module._id,
																module,
																permissions: module.permissions.map((perm) => ({
																	_id: perm,
																	name: perm,
																	status: 'true',
																})),
															};
															const updated = addAccess.filter((a) => a.module._id !== module._id);
														
															setAddAccess([...updated, newAccess]);
														}
													}}
												/> */}
											<input
												type="checkbox"
												className="form-check-input me-2"
												id={`module-checkbox-${module._id}`}
												checked={moduleAccess?.permissions?.every((p) => p.status === 'true') || false}
												onChange={(e) => {
													const isChecked = e.target.checked;

													if (isChecked) {
														// Mark ALL permissions as checked
														const newAccess: Access = {
															_id: module._id,
															module,
															permissions: module.permissions.map((perm) => ({
																_id: perm,
																name: perm,
																status: 'true',
															})),
														};
														const updated = addAccess.filter((a) => a.module._id !== module._id);
														setAddAccess([...updated, newAccess]);
													} else {
														// Mark ALL permissions as unchecked (remove entry)
														setAddAccess(addAccess.filter((access) => access.module._id !== module._id));
													}
												}}
											/>

											<label htmlFor={`module-checkbox-${module._id}`} className="form-check-label fw-bold fs-5 text-white d-flex align-items-center gap-2">
												<File size={18} />
												{capitalizeWords(module.name)}
											</label>
										</div>

										<div className="ms-4">
											{module.permissions.map((permName) => (
												<div key={permName} className="form-check col d-flex align-items-center">
													<input
														type="checkbox"
														className="form-check-input me-2"
														id={`perm-checkbox-${module._id}-${permName}`}
														checked={moduleAccess?.permissions.find((p) => p.name === permName)?.status === 'true'}
														onChange={() => toggleAddPermission(module._id, permName)}
													/>
													<label htmlFor={`perm-checkbox-${module._id}-${permName}`} className="form-check-label text-capitalize">
														{capitalizeWords(permName)}
													</label>
												</div>
											))}
										</div>
									</div>
								);
							})
						)}

						<div className="row mt-4">
							<div className="col d-flex justify-content-end">
								<Button
									className="hrms-gradient-btn hrms-btn"
									onClick={handleSaveRole}

								>
									{id ? 'Update' : 'Save'}
								</Button>
							</div>
						</div>
					</CardBody>
					{/* </Card> */}
				</CardBody>
			</Card>
		</Page>
	);
};

RoleCreate.defaultProps = {
	isFluid: false,
};

export default RoleCreate;
